import { faGrid } from "@fortawesome/pro-light-svg-icons";
import { faChevronLeft } from "@fortawesome/pro-light-svg-icons/faChevronLeft";
import { faChevronRight } from "@fortawesome/pro-light-svg-icons/faChevronRight";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Grid, IconButton, Stack, Typography } from '@mui/material';
import { GenericAbortSignal } from "axios";
import { MprcContainer, MprcFormBarHeader } from "components";
import { ButtonBack } from "components/buttons";
import { MprcFilterBox, MprcFilterCountInfo, MprcFilterLine } from "components/filter";
import { MprcBoxHeader } from "components/MprcBoxHeader";
import { MprcRequestErrorReport } from "components/MprcRequestErrorReport";
import { MprcRibbonListCardsPortalContainer, MprcRibbonListCardsWrapContainer } from "components/MprcRibbonList";
import { MprcRibbonListCardsGridContainer } from "components/MprcRibbonList/MprcRibbonListCardContainers";
import { MprcRibbonTab, MprcRibbonTabs } from "components/MprcRibbonList/MprcRibbonTab";
import { MprcRibbonScroll, MprcRibbonScrollHandle, MprcRibbonSlideReachedPositions } from "components/MprcRibbonScroll";
import { MprcSearchTextField } from "components/MprcSearchTextField";
import { MprcSpinnerIndicator } from "components/MprcSpinnerIndicator";
import { useMprcTheme } from "components/MprcThemeProvider";
import { CarouselCardRenderMode } from "generated/CoreApi_v1";
import useDidUpdateEffect from "hooks/useNoInitialEffect";
import { OUTSIDE_LAYOUT_CONTENT_ID, OUTSIDE_LAYOUT_PORTAL_CONTAINER_ID } from "layouts/common/outside/MprcLayoutOutside";
import { NUMBER_MAX_INT32 } from "lib/LibUtils";
import { isCanceledError } from "providers/CoreApiProvider";
import React, { useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import ReactDOM from "react-dom";
import { useLocation, useNavigate, useSearchParams } from "react-router";
import { GeneralCardLoading } from "views/general/Cards";
import { GeneralCardCompleted } from "views/general/Cards/GeneralCardCompleted";
import { GeneralCardLoadMore } from "views/general/Cards/GeneralCardLoadMore";

/////////////////////////////////////////////////////////////////////////////////////////////////////////

export type MprcRibbonTabbedItem = {
    id: string;
    name: string;
    renderMode: CarouselCardRenderMode;
};

export type MprcRibbonListTabbedHandle = {
    reset: () => void;
    expandItem: (id: string) => void;
    replaceCard: (index: number, card: React.ReactNode, expanded: boolean) => void;
};

const ExpandTabKey = "t";

/////////////////////////////////////////////////////////////////////////////////////////////////////////

interface MprcListRibbonProps {
    ribbonId: number;
    items: MprcRibbonTabbedItem[];
    maxWidth: number;
    gapWidth: number;
    maxItems?: number;
    cardMargin: number;
    onGetCards: (ribbonId: number, carouselId: string, carouselindex: number, renderMode: CarouselCardRenderMode, expanded: boolean, maxItems: number, searchText: string, signal: GenericAbortSignal | undefined) => Promise<React.ReactNode[]>;
    onEmptyDataSet?: () => void;
    onClearCache?: () => void;
}

const MprcRibbonListTabbedComponent: React.ForwardRefRenderFunction<MprcRibbonListTabbedHandle, MprcListRibbonProps> = (props, forwardRef) => {
    const { ribbonId, items, maxWidth, gapWidth, maxItems, cardMargin, onEmptyDataSet, onGetCards } = props;
    const { theme } = useMprcTheme();
    const navigate = useNavigate();
    const location = useLocation();

    const [searchParams, setSearchParams] = useSearchParams();
    const [slidePosition, setSlidePosition] = useState<MprcRibbonSlideReachedPositions[]>(items.map(() => ({ scrollX: 0, start: true, end: true })));
    const [ribbonChildren, setRibbonChildren] = useState<React.ReactNode[] | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [viewError, setViewError] = useState<any>();
    const [currentTab, setCurrentTab] = useState<number>(0);
    const [expandedItem, setExpandedItem] = useState<string | null>(null);
    const abortControllerRef = useRef<AbortController>(undefined);
    const ribbonRef = useRef<MprcRibbonScrollHandle | null>(null);

    const outsideContainer = useRef<HTMLDivElement>(undefined!);
    const outsidePortalContainer = useRef<HTMLDivElement>(undefined!);

    const searchText = useMemo(() => location.state?.searchText ?? "", [location.state]);

    /////////////////////////////////////////////////////////////////////////////////////////////////////////

    useImperativeHandle(forwardRef, () => ({
        reset: () => { setRibbonChildren(null); },
        expandItem: (id: string) => expandTab(id),
        replaceCard: (index: number, card: React.ReactNode) => {
            setRibbonChildren(state => state == null ? null : state.map((c, i) => i != index ? c : card));
        }
    }));

    /////////////////////////////////////////////////////////////////////////////////////////////////////////

    async function load() {

        abortControllerRef.current?.abort();

        abortControllerRef.current = new AbortController();

        setLoading(true);

        try {
            const tab = items[currentTab];

            const c = await onGetCards(
                ribbonId,
                tab.id,
                currentTab,
                tab.renderMode,
                tab.id == expandedItem,
                maxItems ?? NUMBER_MAX_INT32,
                searchText,
                abortControllerRef.current.signal
            );

            setRibbonChildren(c);

            if (c.length == 0 && onEmptyDataSet) onEmptyDataSet();

            setLoading(false);
        }
        catch (e) {
            if (!isCanceledError(e)) {
                setLoading(false);
                setViewError(e);
            }
        }
    }

    ///
    const handleSelectTab = (event: React.SyntheticEvent, newValue: number) => {
        setCurrentTab(newValue);
    };

    ///
    const hideContent = () => {
        outsideContainer.current.classList.add("hide");
        outsidePortalContainer.current.classList.add("show");
    };

    ///
    const undoHide = () => {
        const tab = items[currentTab];
        if (tab.id == expandedItem) {
            // garantir que o evento está ocorrendo na TAB correta

            outsideContainer.current.classList.remove("hide");
            outsidePortalContainer.current.classList.remove("show");

            setExpandedItem(null);
            if (searchParams.has(ExpandTabKey)) {
                navigate(location.pathname, { replace: true });
                setSearchParams(current => {
                    current.delete(ExpandTabKey);
                    return current;
                }, { replace: true });
            }
        }
    };

    ///
    const expandTab = (id: string) => {
        setSearchParams(current => {
            current.set(ExpandTabKey, id);
            return current;
        }, { replace: true });
    };

    ///
    const changeSearchText = (text: string) => {
        navigate(document.location.search, { replace: true, state: { searchText: text } });
    };

    ///
    const RenderExpandedTab = () => {
        return ReactDOM.createPortal(
            <div>
                <MprcBoxHeader>
                    <MprcFormBarHeader
                        actions={[
                            <ButtonBack
                                key="btnBack"
                                rounded
                                variant="text"
                                onClick={undoHide}
                            />,
                            <Typography variant="h2" color="muted">
                                {theme.MprcRibbonList.seeMoreText}
                            </Typography>
                        ]}

                    />
                </MprcBoxHeader>

                <MprcRibbonListCardsPortalContainer>
                    <MprcContainer>
                        <MprcFilterBox>
                            <MprcFilterLine>
                                <Typography variant="h3" >{items[currentTab].name}</Typography>
                            </MprcFilterLine>

                            <MprcSearchTextField
                                autoFocus
                                initialValue={searchText}
                                placeholder={theme.MprcRibbonList.searchPlaceholder}
                                confirmMode="debounce"
                                onConfirm={(text) => changeSearchText(text)}
                            />
                        </MprcFilterBox >
                    </MprcContainer>

                    {
                        loading
                            ? <MprcSpinnerIndicator />
                            : ribbonChildren?.length == 0
                                ? <MprcFilterCountInfo
                                    text={theme.MprcRibbonList.emptyDataText}
                                    variant={"bodyNotice"}
                                    onResetFilter={() => changeSearchText("")}
                                />
                                : <MprcRibbonListCardsGridContainer>
                                    {ribbonChildren}
                                </MprcRibbonListCardsGridContainer>
                    }
                </MprcRibbonListCardsPortalContainer>
            </div>
            , outsidePortalContainer.current);
    };

    /////////////////////////////////////////////////////////////////////////////////////////////////////////

    useEffect(() => {
        if (expandedItem != null) load();
    }, [expandedItem]);

    useEffect(() => {
        outsideContainer.current = document.getElementById(OUTSIDE_LAYOUT_CONTENT_ID) as HTMLDivElement;
        outsidePortalContainer.current = document.getElementById(OUTSIDE_LAYOUT_PORTAL_CONTAINER_ID) as HTMLDivElement;

        return () => {
            // Restaura estado inicial
            undoHide();
        };
    }, []);

    useEffect(() => {
        // Carregar os dados de todas as TABs.
        if (expandedItem == null) load();
    }, [currentTab, items]);

    useDidUpdateEffect(() => {
        // Somente carregar os dados da TAB que estiver expandida.
        // Necessário pois o argumento de Busca fica no State da URL.
        // Assim, quando altera o texto de busca, todas as instâncias deste componente são afetadas.
        // Então foi necessário investigar qual a TAB que está expandida no momento.
        if (expandedItem != null) load();
    }, [searchText]);

    useEffect(() => {
        if (ribbonChildren == null) load();
        else ribbonRef.current?.move(slidePosition[currentTab].scrollX);
    }, [ribbonChildren]);

    useEffect(() => {
        if (!searchParams.has(ExpandTabKey)) {
            undoHide();
        }
        else {
            const id = searchParams.get(ExpandTabKey);
            const tabIndex = items.findIndex(p => p.id == id);
            if (expandedItem == null && tabIndex > -1) {
                setCurrentTab(tabIndex);
                setExpandedItem(id);
                hideContent();
            }
        }
    }, [searchParams]);

    const slideWidth = useMemo(() => {
        const tab = items[currentTab];
        const cardWidth = tab.renderMode == CarouselCardRenderMode.Default ? 220 : 294;
        const w = (cardWidth + gapWidth);
        const maxItems = Math.floor((maxWidth + gapWidth) / w);
        return (w * maxItems) + ((cardMargin / 2) * maxItems) + gapWidth;
    }, [maxWidth]);

    /////////////////////////////////////////////////////////////////////////////////////////////////////////

    const sp = slidePosition[currentTab];

    return (
        <Stack
            direction="column"
            spacing={1}
            sx={{ mb: 2 }}
        >

            <Grid container justifyContent="center" alignItems="center">
                <Grid size="grow" overflow="hidden">
                    <MprcRibbonTabs
                        variant="scrollable"
                        value={currentTab}
                        onChange={handleSelectTab}
                    >
                        {
                            items.map(
                                (item, index) => (
                                    <MprcRibbonTab
                                        className={`ctab-${items[currentTab].id}`}
                                        key={index}
                                        value={index}
                                        label={item.name}
                                        disableRipple
                                        disabled={loading}
                                    />
                                )
                            )
                        }
                    </MprcRibbonTabs>
                </Grid>

                <Grid>
                    {
                        (sp.start == false || sp.end == false) && items[currentTab].renderMode == CarouselCardRenderMode.Default &&
                        <IconButton onClick={() => expandTab(items[currentTab].id)} disabled={loading}>
                            <FontAwesomeIcon icon={faGrid} title={theme.MprcRibbonList.expandTooltip} />
                        </IconButton>
                    }
                    {
                        (sp.start == false || sp.end == false) &&
                        <>
                            <IconButton title={theme.MprcRibbonList.slidePreviousTooltip} disabled={loading || sp.start} onClick={() => ribbonRef.current?.slide(0 - slideWidth)}><FontAwesomeIcon icon={faChevronLeft} /></IconButton>
                            <IconButton title={theme.MprcRibbonList.slideNextTooltip} disabled={loading || sp.end} onClick={() => ribbonRef.current?.slide(slideWidth)}><FontAwesomeIcon icon={faChevronRight} /></IconButton>
                        </>
                    }
                </Grid>
            </Grid>

            {
                viewError
                    ? <MprcRequestErrorReport error={viewError} />
                    : loading && items[currentTab].id != expandedItem
                        ? (
                            <div>
                                <MprcRibbonListCardsWrapContainer gapWidth={gapWidth}>
                                    <GeneralCardLoading />
                                </MprcRibbonListCardsWrapContainer>
                            </div>
                        )
                        : items[currentTab].id == expandedItem
                            ? RenderExpandedTab()
                            : (
                                <MprcRibbonScroll
                                    showNavigationButtons={false}
                                    ref={ribbonRef}
                                    onPositionChanged={
                                        (position) => {
                                            setSlidePosition(
                                                slidePosition.map((item, index) => { return index == currentTab ? position : item; })
                                            );
                                        }
                                    }
                                >
                                    <MprcRibbonListCardsWrapContainer gapWidth={gapWidth} flexWrap="nowrap">
                                        {
                                            ribbonChildren?.length == 0
                                                ? <GeneralCardCompleted marginWidth={0} />
                                                : maxItems == undefined
                                                    ? ribbonChildren
                                                    : (
                                                        <>
                                                            {React.Children.map(ribbonChildren, (c, index) => index < maxItems ? c : null)}
                                                            {React.Children.count(ribbonChildren) > maxItems && items[currentTab].renderMode == CarouselCardRenderMode.Default &&
                                                                <GeneralCardLoadMore onClick={() => expandTab(items[currentTab].id)} />
                                                            }
                                                        </>
                                                    )
                                        }
                                    </MprcRibbonListCardsWrapContainer>
                                </MprcRibbonScroll>
                            )
            }
        </Stack>
    );
};

export const MprcRibbonListTabbed = React.forwardRef(MprcRibbonListTabbedComponent);
